/** Shared game data. No Phaser, no clock, no I/O. */

export type ArchetypeId = 'ironbound' | 'veilweaver' | 'wildkin' | 'shadecaller';

export type SubSpecId =
  | 'bulwark'
  | 'chainwarden'
  | 'forgeheart'
  | 'duelist'
  | 'siegebreaker'
  | 'banner'
  | 'quartermaster'
  | 'oathkeeper'
  | 'loomcaster'
  | 'hourglass'
  | 'foldwalker'
  | 'omen'
  | 'stillpoint'
  | 'mirrortailor'
  | 'unraveler'
  | 'fatebinder'
  | 'rootmother'
  | 'packcaller'
  | 'barkhide'
  | 'sporelord'
  | 'seasonturner'
  | 'stormhorn'
  | 'mosswarden'
  | 'hivekeeper'
  | 'debtcollector'
  | 'echo'
  | 'lanternless'
  | 'hollowshepherd'
  | 'grave'
  | 'umbralanchor'
  | 'whisperthief'
  | 'dusk';

export type SlotId = 'primary' | 'secondary' | 'tertiary';

export type GearSlot = 'weapon' | 'offhand' | 'helm' | 'chest' | 'gloves' | 'boots';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type Tier = 1 | 2 | 3;

export type TargetKind = 'self' | 'lowestHp' | 'highestHp' | 'boss';

export type Condition =
  | { kind: 'always' }
  | { kind: 'selfHpBelow'; pct: number }
  | { kind: 'enemyCountAtLeast'; count: number }
  | { kind: 'targetIsEliteOrBoss' }
  | { kind: 'targetHasArmor' };

export interface Rule {
  id: string;
  condition: Condition;
  abilityId: string;
  target: TargetKind;
}

export interface BuildState {
  primary: SubSpecId | null;
  secondary: SubSpecId | null;
  tertiary: SubSpecId | null;
  /** Ranks currently invested in the equipped sub-specs. Refunded on re-braid. */
  ranks: Partial<Record<SubSpecId, number>>;
}

export interface ItemStats {
  hp: number;
  attack: number;
  armor: number;
  armorBreak: number;
  heal: number;
}

export interface Item {
  id: string;
  name: string;
  slot: GearSlot;
  tier: Tier;
  rarity: Rarity;
  /** 0–10. +1 through +10 always succeed. Gear is never destroyed. */
  enhance: number;
  stats: ItemStats;
  /** Set by gear_action. A locked item is never recycled. */
  locked?: boolean;
  /** Boss signature pieces are never recycled, even when no set is in the game yet. */
  isBossSet?: boolean;
  /** Set id for a boss signature piece. Empty or absent means it is not a set piece. */
  setId?: string | null;
}

export interface PityState {
  /** Epic or better drops since the last Legendary. */
  epicPlusSinceLegendary: number;
}

export interface Currencies {
  /** Earned from fights only. There is no premium currency. */
  gold: number;
  /** Earned from clears and salvage only. */
  forgeDust: number;
}

export type EquippedGear = Record<GearSlot, Item | null>;

export interface HeroState {
  name: string;
  level: number;
  xp: number;
  unspentPoints: number;
  build: BuildState;
  rules: Rule[];
}

export interface ProgressState {
  bestCleared: number;
  pushStage: number;
  /** Highest non-wall stage cleared 3 times in a row. 0 until one exists. */
  farmStage: number;
  streakStage: number;
  clearStreak: number;
  failStreak: number;
  mode: 'push' | 'farm';
  /** Non-wall stage being repeated so the echo can farm it. 0 if not drilling. */
  drillStage: number;
  notice: string | null;
}

export interface IdleRates {
  goldPerMin: number;
  xpPerMin: number;
  dustPerMin: number;
  dropRollsPerMin: number;
}

export interface IdleSnapshot {
  farmStageId: number;
  rates: IdleRates;
  /** Earned cap in hours. Base is 8. Never a paid field. */
  capHours: number;
  seed: number;
}

/** Last accepted farm batch. The server writes this. The client sends the next seq. */
export interface FarmBatchReceipt {
  seq: number;
  stageId: number;
  paid: number;
  requested: number;
  capped: number;
  outcome: 'victory' | 'defeat';
  gold: number;
  xp: number;
  dust: number;
  dropRolls: number;
  levelsGained: number;
}

export interface FarmBatchState {
  /** Last accepted sequence. 0 before the first batch. */
  seq: number;
  /** Server milliseconds of the last settle, single farm clear, or idle claim. */
  settledAtMs: number;
  receipt: FarmBatchReceipt | null;
}

export interface IdleMeta {
  lastActiveAtMs: number | null;
  pendingClaim: boolean;
  snapshot: IdleSnapshot | null;
  /** Bumped for each fight so seeds change without using the clock. */
  fightSerial: number;
  /** Absent on saves written before farm batches. */
  farmBatch?: FarmBatchState | null;
}

export interface SaveData {
  hero: HeroState;
  gear: {
    equipped: EquippedGear;
    bag: Item[];
    pity: PityState;
    nextItemId: number;
    /** New drops below this rarity are recycled on the server. Null means off. */
    autoRecycleBelow: Rarity | null;
  };
  currencies: Currencies;
  progress: ProgressState;
  idle: IdleMeta;
}

export interface SaveFile {
  version: number;
  data: SaveData;
}

export const GEAR_SLOTS: GearSlot[] = ['weapon', 'offhand', 'helm', 'chest', 'gloves', 'boots'];

export const RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

export const SLOT_IDS: SlotId[] = ['primary', 'secondary', 'tertiary'];

export const SLOT_WEIGHT: Record<SlotId, number> = {
  primary: 0.5,
  secondary: 0.3,
  tertiary: 0.2,
};

/** §3.2 rank caps. Heart 30, Hand 20, Whisper 12. */
export const SLOT_MAX_RANK: Record<SlotId, number> = {
  primary: 30,
  secondary: 20,
  tertiary: 12,
};

export const RULE_SLOTS = 4;
export const LEVEL_CAP = 30;
/** Points granted at each level, including level 1. Spent plus unspent equals level times this. */
export const POINTS_PER_LEVEL = 1;
export const MAX_ENHANCE = 10;
export const BAG_LIMIT = 24;

/** Guaranteed Legendary on the next Epic+ drop after this many Epic+ drops without one. */
export const LEGENDARY_PITY = 10;

export const DEFAULT_OFFLINE_CAP_HOURS = 8;
export const FULL_RATE_MS = 2 * 60 * 60 * 1000;
export const OFFLINE_EFFICIENCY_AFTER = 0.75;

export const TICK_SECONDS = 0.25;

export function emptyGear(): EquippedGear {
  return {
    weapon: null,
    offhand: null,
    helm: null,
    chest: null,
    gloves: null,
    boots: null,
  };
}
